/**
 * Lenz — ETA dinámica para la product page.
 *
 * Muestra tres filas en la tarjeta #lenz-product-eta-card:
 *   1. Envío regular  — rango de días útiles según Lima (sin departamento elegido aún)
 *   2. Express        — HOY o siguiente día útil con cuenta regresiva al cutoff
 *   3. Retiro tienda  — HOY o próximo día disponible
 *
 * Misma lógica de negocio que lenz-shipping-eta.js (carrito).
 * No hay dependencia entre los dos archivos; se mantienen separados para
 * poder evolucionar independientemente.
 */
(function () {
  'use strict';

  var TZ = 'America/Lima';

  var EXPRESS_CUTOFF = { mon: 17, tue: 17, wed: 17, thu: 17, fri: 17, sat: 13, sun: null };
  var STORE_HOURS = {
    mon: { open: 9,  close: 18 },
    tue: { open: 9,  close: 18 },
    wed: { open: 9,  close: 18 },
    thu: { open: 9,  close: 18 },
    fri: { open: 9,  close: 18 },
    sat: { open: 10, close: 14 },
    sun: null,
  };

  // Rango Lima por defecto (sin departamento elegido)
  var LIMA_DAYS = { min: 2, max: 2 };
  var PROVINCE_MAX = 7;

  // Tiempo de preparación del pedido para retiro en tienda.
  // Si quedan al menos PICKUP_PREP_HOURS antes del cierre y la tienda está
  // abierta, el cliente puede recoger HOY mismo. Mismo valor que en
  // lenz-shipping-eta.js para mantener consistencia entre product page y carrito.
  // Cortes resultantes: L-V 17:00, S 13:00.
  var PICKUP_PREP_HOURS = 1;

  // Rango para productos "A pedido" (24-48h = 1-2 días útiles)
  var MADE_TO_ORDER_DAYS = { min: 1, max: 2 };

  // Rango para productos "Importación" (10-15 días útiles)
  var IMPORTED_DAYS = { min: 10, max: 15 };

  var DAY_KEYS      = ['sun','mon','tue','wed','thu','fri','sat'];
  var DAY_NAMES_ES  = ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'];
  var MONTH_NAMES_ES = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];

  // ── Feriados y cierres especiales ────────────────────────────────────────
  //
  // Formato: 'YYYY-MM-DD' (hora Lima). La tienda no opera estos días:
  // no hay retiro, no hay despacho express, no cuentan como días útiles.
  //
  // CÓMO MANTENER ESTA LISTA:
  //   1. En diciembre agrega los feriados del año siguiente.
  //   2. Para cierres puntuales de la tienda (inventario, vacaciones, etc.)
  //      añade la fecha en CUSTOM_CLOSURES.
  //   3. Elimina fechas pasadas cuando quieras limpiar (no afecta el cálculo).

  var HOLIDAYS = [
    // ── 2025 ──────────────────────────────────────────────────────────────
    '2025-01-01', // Año Nuevo
    '2025-04-17', // Jueves Santo
    '2025-04-18', // Viernes Santo
    '2025-05-01', // Día del Trabajo  ← mañana
    '2025-06-07', // Batalla de Arica (no laborable)
    '2025-06-16', // Día del Padre (no laborable Lima Metropolitana)
    '2025-06-29', // San Pedro y San Pablo
    '2025-07-23', // (día no laborable por proximidad a Fiestas Patrias)
    '2025-07-28', // Fiestas Patrias
    '2025-07-29', // Fiestas Patrias
    '2025-08-25', // (día no laborable por proximidad a Santa Rosa)
    '2025-08-30', // Santa Rosa de Lima
    '2025-10-08', // Combate de Angamos
    '2025-11-01', // Todos los Santos
    '2025-12-08', // Inmaculada Concepción
    '2025-12-09', // Batalla de Ayacucho
    '2025-12-25', // Navidad
    // ── 2026 ──────────────────────────────────────────────────────────────
    '2026-01-01', // Año Nuevo
    '2026-04-02', // Jueves Santo
    '2026-04-03', // Viernes Santo
    '2026-05-01', // Día del Trabajo
    '2026-06-07', // Batalla de Arica y Día de la Bandera
    '2026-06-29', // San Pedro y San Pablo
    '2026-07-23', // Día de la Fuerza Aérea del Perú
    '2026-07-28', // Fiestas Patrias
    '2026-07-29', // Fiestas Patrias
    '2026-08-06', // Batalla de Junín
    '2026-08-30', // Santa Rosa de Lima
    '2026-10-08', // Combate de Angamos
    '2026-11-01', // Todos los Santos
    '2026-12-08', // Inmaculada Concepción
    '2026-12-09', // Batalla de Ayacucho
    '2026-12-25', // Navidad
  ];

  // Cierres puntuales de la tienda (inventario, vacaciones, etc.)
  // Mismo formato: 'YYYY-MM-DD'
  var CUSTOM_CLOSURES = [
    // Ejemplo: '2025-12-26', // Cierre post-navidad
  ];

  // Set de todas las fechas no operativas para lookup O(1)
  var CLOSED_DATES = (function () {
    var s = {};
    var all = HOLIDAYS.concat(CUSTOM_CLOSURES);
    for (var i = 0; i < all.length; i++) s[all[i]] = true;
    return s;
  })();

  /** Devuelve la clave 'YYYY-MM-DD' de un Date para comparar con CLOSED_DATES. */
  function dateKey(d) {
    var y = d.getFullYear();
    var m = d.getMonth() + 1;
    var day = d.getDate();
    return y + '-' + (m < 10 ? '0' + m : m) + '-' + (day < 10 ? '0' + day : day);
  }

  function isHoliday(d)      { return !!CLOSED_DATES[dateKey(d)]; }

  // ── Tiempo ──────────────────────────────────────────────────────────────

  function nowInLima() {
    var fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
    var parts = {};
    fmt.formatToParts(new Date()).forEach(function (p) {
      if (p.type !== 'literal') parts[p.type] = p.value;
    });
    var hour = parseInt(parts.hour, 10);
    if (hour === 24) hour = 0;
    return new Date(
      parseInt(parts.year,   10),
      parseInt(parts.month,  10) - 1,
      parseInt(parts.day,    10),
      hour,
      parseInt(parts.minute, 10),
      parseInt(parts.second, 10)
    );
  }

  function dayKey(d)         { return DAY_KEYS[d.getDay()]; }
  function isSunday(d)       { return d.getDay() === 0; }
  /** Día hábil = no domingo Y no feriado/cierre de tienda. */
  function isBusinessDay(d)  { return !isSunday(d) && !isHoliday(d); }

  function addDays(d, n) {
    var r = new Date(d.getTime());
    r.setDate(r.getDate() + n);
    return r;
  }
  function addBusinessDays(d, n) {
    var r = new Date(d.getTime()), added = 0;
    while (added < n) { r = addDays(r, 1); if (isBusinessDay(r)) added++; }
    return r;
  }
  function nextBusinessDay(d) { return addBusinessDays(d, 1); }

  function formatDate(d) {
    return DAY_NAMES_ES[d.getDay()] + ' ' + d.getDate() + ' de ' + MONTH_NAMES_ES[d.getMonth()];
  }
  function shortDate(d) {
    return d.getDate() + ' de ' + MONTH_NAMES_ES[d.getMonth()];
  }
  /** Versión compacta: "lunes 27" (sin mes) — para etiquetas con poco espacio. */
  function formatDateCompact(d) {
    return DAY_NAMES_ES[d.getDay()] + ' ' + d.getDate();
  }
  /** Versión abreviada: "mié. 29" — para labels largos que no caben en una línea. */
  var DAY_ABBREV_ES = ['dom.','lun.','mar.','mié.','jue.','vie.','sáb.'];
  function formatDateAbbrev(d) {
    return DAY_ABBREV_ES[d.getDay()] + ' ' + d.getDate();
  }

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }
  function fmtHour(h) {
    var hour = Math.floor(h);
    var min  = Math.round((h - hour) * 60);
    var suf  = hour < 12 ? 'am' : 'pm';
    var h12  = hour % 12 === 0 ? 12 : hour % 12;
    return h12 + (min ? ':' + pad2(min) : '') + suf;
  }

  function msUntilHour(now, hourDecimal) {
    var target = new Date(now.getTime());
    var h = Math.floor(hourDecimal);
    var m = Math.round((hourDecimal - h) * 60);
    target.setHours(h, m, 0, 0);
    return target.getTime() - now.getTime();
  }

  /** Formato HH:MM:SS tipo reloj digital (siempre 2 dígitos). */
  function formatCountdown(ms) {
    if (ms < 0) ms = 0;
    var totalSec = Math.floor(ms / 1000);
    var h = Math.floor(totalSec / 3600);
    var m = Math.floor((totalSec % 3600) / 60);
    var s = totalSec % 60;
    return pad2(h) + ':' + pad2(m) + ':' + pad2(s);
  }

  // ── Cálculo de cada fila ────────────────────────────────────────────────

  function calcRegular(now) {
    // Si hoy es domingo o feriado, el despacho parte del siguiente día hábil.
    var start = isBusinessDay(now) ? now : nextBusinessDay(now);
    var limaDate  = addBusinessDays(start, LIMA_DAYS.min);
    var maxDate   = addBusinessDays(start, PROVINCE_MAX);
    return {
      label: 'Envío por agencia:',
      value: 'A partir del ' + formatDateAbbrev(limaDate),  // "mié. 29" (abreviado para que quepa en una línea)
      sub:   'En provincia: hasta el ' + shortDate(maxDate) + ' según destino.',
    };
  }

  function calcMadeToOrder(now) {
    var start = isBusinessDay(now) ? now : nextBusinessDay(now);
    var minDate = addBusinessDays(start, MADE_TO_ORDER_DAYS.min);
    return {
      label: 'A pedido:',
      value: 'A partir del ' + formatDateAbbrev(minDate),
      sub:   '24 a 48 horas tras confirmar tu pedido.',
    };
  }

  function calcImported(now) {
    var start = isBusinessDay(now) ? now : nextBusinessDay(now);
    var minDate = addBusinessDays(start, IMPORTED_DAYS.min);
    return {
      label: 'Importación:',
      value: 'A partir del ' + shortDate(minDate),
      sub:   IMPORTED_DAYS.min + ' a ' + IMPORTED_DAYS.max + ' días útiles desde la confirmación.',
    };
  }

  function calcExpress(now) {
    var dk = dayKey(now);
    var cutoff = EXPRESS_CUTOFF[dk];
    // Hoy solo aplica express si no es feriado y hay tiempo antes del cutoff.
    if (cutoff != null && !isHoliday(now)) {
      var ms = msUntilHour(now, cutoff);
      if (ms > 5 * 60 * 1000) {
        return {
          label:   'Envío express:',
          value:   'Recíbelo hoy',
          sub:     'Solo Lima/Callao.',
          urgency: { ms: ms, cutoffHour: cutoff },
        };
      }
    }
    // Siguiente día hábil (ya excluye feriados via isBusinessDay).
    var nbd = nextBusinessDay(now);
    return {
      label:   'Envío express:',
      value:   'Recíbelo el ' + formatDateCompact(nbd),
      sub:     'Solo Lima/Callao.',
      urgency: null,
    };
  }

  function calcPickup(now) {
    var dk  = dayKey(now);
    var hrs = STORE_HOURS[dk];
    var h   = now.getHours() + now.getMinutes() / 60;
    // HOY solo si: la tienda tiene horario, no es feriado, y queda al menos
    // PICKUP_PREP_HOURS antes del cierre (1h de margen de preparación).
    if (hrs && !isHoliday(now) && h >= hrs.open && h <= hrs.close - PICKUP_PREP_HOURS) {
      return {
        label: 'Retiro en tienda:',
        value: 'HOY',
        sub:   'Jr. Garcilaso de la Vega 1721.',
      };
    }
    // Buscar el próximo día en que la tienda esté abierta Y no sea feriado.
    var d = new Date(now.getTime());
    for (var i = 0; i < 14; i++) {
      d = addDays(d, 1);
      var h2 = STORE_HOURS[dayKey(d)];
      if (h2 && !isHoliday(d)) {
        return {
          label: 'Retiro en tienda:',
          value: formatDateCompact(d),
          sub:   'Jr. Garcilaso de la Vega 1721.',
        };
      }
    }
    return { label: 'Retiro en tienda:', value: 'consulta disponibilidad', sub: 'Jr. Garcilaso de la Vega 1721.' };
  }

  // ── DOM ─────────────────────────────────────────────────────────────────

  function setText(id, value) {
    var el = document.getElementById(id);
    if (el) el.textContent = value;
  }
  function setHidden(id, hidden) {
    var el = document.getElementById(id);
    if (el) el.hidden = hidden;
  }

  var _expressCountdownHour = null;

  // SVG icons inline (heredan currentColor del contenedor)
  var ICON_CLOCK =
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"' +
    ' stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">' +
    '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>' +
    '</svg>';
  var ICON_PLANE =
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"' +
    ' stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>' +
    '</svg>';
  // Calendario — modo preventa (la fecha viene de los metafields, no se calcula)
  var ICON_CALENDAR =
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"' +
    ' stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">' +
    '<rect x="3" y="4" width="18" height="18" rx="2"/>' +
    '<line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>' +
    '<line x1="3" y1="10" x2="21" y2="10"/>' +
    '</svg>';

  // X roja en círculo para indicar "no disponible"
  var ICON_X_CIRCLE =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="#dc2626">' +
    '<path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm-3.97-12.03a.75.75 0 0 1 1.06 0L10 8.94l2.91-2.97a.75.75 0 1 1 1.07 1.06L11.06 10l2.92 2.97a.75.75 0 1 1-1.07 1.06L10 11.06l-2.91 2.97a.75.75 0 1 1-1.07-1.06L8.94 10 6.03 7.03a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd"/>' +
    '</svg>';

  /** Aplica un payload {label, value, sub} a una fila identificada por prefix. */
  function applyRow(prefix, data) {
    setText('lenz-peta-' + prefix + '-label', data.label);
    setText('lenz-peta-' + prefix + '-value', data.value);
    setText('lenz-peta-' + prefix + '-sub',   data.sub || '');
  }

  /** Marca una fila como "no disponible" — X roja + texto en value y limpia sub. */
  function markRowUnavailable(prefix, label) {
    var row = document.getElementById('lenz-peta-' + prefix);
    if (!row) return;
    row.classList.add('lenz-product-eta-row--unavailable');
    row.hidden = false;
    var check = row.querySelector('.lenz-product-eta-row__check');
    if (check) check.innerHTML = ICON_X_CIRCLE;
    applyRow(prefix, { label: label, value: 'No disponible', sub: '' });
  }

  function applySpecialMode(now, kind) {
    // Fila 1: el modo especial (a pedido / importación) en la posición de regular
    var data = kind === 'imported' ? calcImported(now) : calcMadeToOrder(now);
    applyRow('regular', data);

    var regularIcon = document.querySelector('#lenz-peta-regular .lenz-product-eta-row__icon');
    if (regularIcon) {
      regularIcon.innerHTML = kind === 'imported' ? ICON_PLANE : ICON_CLOCK;
    }

    // Fila 2: transformar pickup a "Retiro en tienda: No disponible" con X roja
    markRowUnavailable('pickup', 'Retiro en tienda:');

    // Express oculto (no aplica a productos sin stock local)
    var expressRow = document.getElementById('lenz-peta-express');
    if (expressRow) expressRow.hidden = true;
    _expressCountdownHour = null;
  }

  /** Modo PREVENTA: el rango de entrega viene del Liquid (metafields
   *  lenz.preventa_desde/hasta) vía data-preorder-range — no se calcula aquí
   *  porque las fechas son una promesa comercial, no una función del calendario.
   *  Fila regular = rango con ícono calendario; pickup = "Al llegar la preventa"
   *  con check verde (sí habrá retiro, solo que después); express oculto
   *  (no aplica a stock que aún no existe). */
  function applyPreorderMode(card) {
    var range = card.getAttribute('data-preorder-range') || '';
    applyRow('regular', {
      label: 'Entrega estimada:',
      value: range,
      sub:   '',
    });

    var regularIcon = document.querySelector('#lenz-peta-regular .lenz-product-eta-row__icon');
    if (regularIcon) regularIcon.innerHTML = ICON_CALENDAR;

    // Pickup disponible (check verde, no X): el retiro aplica cuando llegue el lote
    applyRow('pickup', { label: 'Retiro en tienda:', value: 'Al llegar la preventa', sub: '' });

    // Express oculto (no hay stock local que despachar hoy)
    var expressRow = document.getElementById('lenz-peta-express');
    if (expressRow) expressRow.hidden = true;
    _expressCountdownHour = null;
  }

  /** Producto agotado en Shopify: las 3 opciones de entrega quedan no disponibles
   *  (a diferencia de imported/made-to-order, donde el envío regular sí aplica
   *  con un ETA largo, aquí ni siquiera podemos prometer una fecha). */
  function applyOutOfStockMode() {
    markRowUnavailable('pickup',  'Retiro en tienda:');
    markRowUnavailable('express', 'Envío express:');
    markRowUnavailable('regular', 'Envío por agencia:');

    // Esconder el countdown de urgencia del express si estaba activo
    setHidden('lenz-peta-express-urgency', true);
    _expressCountdownHour = null;
  }

  function render() {
    var now = nowInLima();
    var card = document.getElementById('lenz-product-eta-card');
    var mode = card && card.getAttribute('data-mode');

    if (mode === 'out-of-stock') {
      applyOutOfStockMode();
      return;
    }

    if (mode === 'preorder') {
      applyPreorderMode(card);
      return;
    }

    if (mode === 'imported' || mode === 'made-to-order') {
      applySpecialMode(now, mode);
      return;
    }

    applyRow('pickup',  calcPickup(now));

    var express = calcExpress(now);
    applyRow('express', express);

    if (express.urgency && express.urgency.ms > 0) {
      setHidden('lenz-peta-express-urgency', false);
      setText('lenz-peta-express-countdown', formatCountdown(express.urgency.ms));
      _expressCountdownHour = express.urgency.cutoffHour;
    } else {
      setHidden('lenz-peta-express-urgency', true);
      _expressCountdownHour = null;
    }

    applyRow('regular', calcRegular(now));
  }

  function tickCountdown() {
    if (_expressCountdownHour === null) return;
    var now = nowInLima();
    var ms  = msUntilHour(now, _expressCountdownHour);
    if (ms <= 0) {
      render();
      return;
    }
    setText('lenz-peta-express-countdown', formatCountdown(ms));
  }

  /**
   * Fix runtime: el theme aplica `position: sticky` al .product-gallery en
   * desktop. Si la galería vive dentro de .lenz-product-left junto con la
   * tarjeta de ETA, el sticky hace que la galería se quede fija al scrollear
   * y la tarjeta se monte detrás de ella. Forzamos position: static en el
   * elemento directamente. Esto evita la dependencia del cache de CSS de
   * Shopify (que puede tardar en invalidarse).
   */
  function disableGallerySticky() {
    var leftWrap = document.querySelector('.lenz-product-left');
    if (!leftWrap) return;
    var gallery = leftWrap.querySelector('.product-gallery');
    if (!gallery) return;
    gallery.style.setProperty('position', 'static', 'important');
    gallery.style.setProperty('top', 'auto', 'important');
  }

  /**
   * Actualiza data-mode y las clases del card según la variante seleccionada.
   * Se llama desde el listener de variant:change para que el ETA refleje
   * si la variante concreta está agotada (aunque product.available sea true
   * porque hay otras variantes con stock).
   */
  function updateModeFromVariant(variant, productTags) {
    var card = document.getElementById('lenz-product-eta-card');
    if (!card) return;

    var tags = productTags || [];

    // Calcular el modo según la misma lógica de prioridad del Liquid:
    //   out-of-stock > preorder > imported > made-to-order > stock
    var newMode = 'stock';
    if (tags.indexOf('importacion') !== -1) newMode = 'imported';
    if (tags.indexOf('a-pedido') !== -1)    newMode = 'made-to-order';
    // Preventa: además del tag exigimos data-preorder-range en el card (el
    // Liquid solo lo emite si los metafields de fechas existen — así el JS
    // no puede activar preorder sin un rango que mostrar).
    if (tags.indexOf('preventa') !== -1 && card.hasAttribute('data-preorder-range')) newMode = 'preorder';
    // La variante agotada pisa cualquier tag
    if (variant && variant.available === false) newMode = 'out-of-stock';

    // Actualizar data-mode y clase del card
    var oldMode = card.getAttribute('data-mode');
    if (oldMode !== newMode) {
      card.setAttribute('data-mode', newMode);
      // Remover clase anterior y añadir la nueva
      ['stock', 'preorder', 'imported', 'made-to-order', 'out-of-stock'].forEach(function (m) {
        card.classList.remove('lenz-product-eta-card--' + m);
      });
      card.classList.add('lenz-product-eta-card--' + newMode);

      // Restaurar check verde en todas las filas antes de re-renderizar
      // (pueden haber sido marcadas como no disponibles en el estado anterior)
      var ICON_CHECK =
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="#16a34a">' +
        '<path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clip-rule="evenodd"/>' +
        '</svg>';
      ['pickup', 'express', 'regular'].forEach(function (prefix) {
        var row = document.getElementById('lenz-peta-' + prefix);
        if (!row) return;
        row.classList.remove('lenz-product-eta-row--unavailable');
        row.hidden = false;
        var check = row.querySelector('.lenz-product-eta-row__check');
        if (check) check.innerHTML = ICON_CHECK;
      });
    }

    render();
  }

  function onVariantChange(event) {
    var detail  = event.detail || {};
    var variant = detail.variant;
    var product = detail.product;
    if (!variant || !product) return;
    // Solo actuar si el card ETA está en la página
    if (!document.getElementById('lenz-product-eta-card')) return;
    updateModeFromVariant(variant, product.tags || []);
  }

  function init() {
    if (!document.getElementById('lenz-product-eta-card')) return;
    disableGallerySticky();
    render();
    setInterval(tickCountdown, 1000);
    window.addEventListener('focus', render);
    // Re-aplicar tras resize (puede cambiar de mobile a desktop sin re-render)
    window.addEventListener('resize', disableGallerySticky);
    // Escuchar cambios de variante para actualizar el modo ETA dinámicamente.
    // Necesario porque product.available (usado en Liquid) es true si CUALQUIER
    // variante tiene stock; aquí necesitamos saber si la variante concreta está
    // agotada para mostrar el ETA correcto.
    document.addEventListener('variant:change', onVariantChange, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
